/**
 * Notifications settings section — mention, owner, and push notification toggles.
 */

import { useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import { useOrg } from '@/contexts/OrgContext';
import { useConnectedAccounts } from '@/hooks/integrations/useConnectedAccounts';
import { useSlackStatus } from '@/hooks/integrations/useSlackStatus';
import { useProfile } from '@/hooks/profile/useProfile';
import { ButtonList } from '@/lib/base/button-list';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { Toggle } from '@/lib/base/forms/toggle';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { DEFAULT_NOTIFICATION_CHANNELS } from '@/lib/profile/constants';
import type {
  NotificationChannel,
  NotificationType,
} from '@/lib/profile/types';
import { useViewHealthMarker } from '@/lib/view-health';

import { useUser } from '../../../../allauth_auth';
import { NotificationSettings } from '../../profile/components/NotificationSettings';

const DIGEST_CADENCE_OPTIONS = [
  { id: 'daily', label: 'Daily (weekdays)' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'none', label: 'Off' },
];

const NOTIFICATION_TYPE_META: Array<{
  id: NotificationType;
  label: string;
  description: string;
}> = [
  {
    id: 'proposals',
    label: 'AI Actions notifications',
    description: 'Where to send notifications when you have new AI actions',
  },
  {
    id: 'digests',
    label: 'Digest delivery',
    description: 'Where to send your activity digests',
  },
];

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  push: 'Push',
  email: 'Email',
  slack: 'Slack',
};

const ALL_CHANNELS: NotificationChannel[] = ['push', 'email', 'slack'];

const LOGIN_EMAIL_OPTION_ID = 'login-email';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/** Which channels are valid for each notification type. Digests don't support push. */
const CHANNELS_PER_TYPE: Record<NotificationType, NotificationChannel[]> = {
  proposals: ['push', 'email', 'slack'],
  digests: ['email', 'slack'],
};

export function NotificationsSection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  useViewHealthMarker('settings-section-ready');
  const {
    formData,
    membershipId,
    membershipLoadError,
    notifyMentionSaving,
    notifyOwnedSaving,
    setNotifyMentionEnabled,
    setNotifyOwnedEnabled,
    digestCadenceSaving,
    setDigestCadence,
    setNotificationChannel,
    notificationChannelsSaving,
    setNotificationEmailAccount,
    notificationEmailSaving,
    notificationEmailError,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });

  const { data: slackStatus } = useSlackStatus(currentOrg?.id);
  const { accounts } = useConnectedAccounts({ orgId: currentOrg?.id });

  const availableChannelsForType = useCallback(
    (type: NotificationType): NotificationChannel[] => {
      const allowed = CHANNELS_PER_TYPE[type] ?? ALL_CHANNELS;
      return allowed.filter(ch => ch !== 'slack' || slackStatus?.connected);
    },
    [slackStatus]
  );

  const channelHandlers = useMemo<
    Record<string, (enabled: boolean) => void>
  >(() => {
    const handlers: Record<string, (enabled: boolean) => void> = {};
    for (const { id: type } of NOTIFICATION_TYPE_META) {
      for (const channel of ALL_CHANNELS) {
        handlers[`${type}:${channel}`] = (enabled: boolean) => {
          setNotificationChannel(type, channel, enabled).catch(() => {});
        };
      }
    }
    return handlers;
  }, [setNotificationChannel]);

  const handleNotifyMentionToggle = useCallback(
    (enabled: boolean) => {
      setNotifyMentionEnabled(enabled).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setNotifyMentionEnabled]
  );

  const handleNotifyOwnedToggle = useCallback(
    (enabled: boolean) => {
      setNotifyOwnedEnabled(enabled).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setNotifyOwnedEnabled]
  );

  const handleDigestCadenceChange = useCallback(
    (key: Key | null) => {
      if (key === null) {
        return;
      }
      setDigestCadence(String(key) as 'daily' | 'weekly' | 'none').catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setDigestCadence]
  );

  const distinctAccounts = useMemo(() => {
    const loginEmail = user?.email ? normalizeEmail(user.email) : '';
    if (!loginEmail) {
      return accounts;
    }
    return accounts.filter(
      account => normalizeEmail(account.email) !== loginEmail
    );
  }, [accounts, user?.email]);

  const notificationEmailOptions = useMemo(
    () => [
      {
        id: LOGIN_EMAIL_OPTION_ID,
        label: user?.email ? `Login email (${user.email})` : 'Login email',
      },
      ...distinctAccounts.map(account => ({
        id: String(account.id),
        label: account.email || String(account.id),
      })),
    ],
    [distinctAccounts, user?.email]
  );

  const notificationEmailSelectedKey = useMemo(() => {
    const stored = formData.notification_social_account_id;
    if (
      stored != null &&
      distinctAccounts.some(account => account.id === stored)
    ) {
      return String(stored);
    }
    return LOGIN_EMAIL_OPTION_ID;
  }, [distinctAccounts, formData.notification_social_account_id]);

  const handleNotificationEmailChange = useCallback(
    (key: Key | null) => {
      if (key === null) {
        return;
      }
      const value = key === LOGIN_EMAIL_OPTION_ID ? null : Number(key);
      setNotificationEmailAccount(value).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setNotificationEmailAccount]
  );

  function isSelected(
    type: NotificationType,
    channel: NotificationChannel
  ): boolean {
    const channels =
      formData.notification_channels[type] ??
      DEFAULT_NOTIFICATION_CHANNELS[type] ??
      [];
    return channels.includes(channel);
  }

  return (
    <div className='space-y-8'>
      {/* Section header */}
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Notifications</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Control which notifications you receive.
        </p>
      </div>

      <div className='space-y-6'>
        {/* Error Alert */}
        {membershipLoadError && (
          <AlertFloating
            color='error'
            title={membershipLoadError}
            description='Notification preferences cannot be changed until this issue is resolved.'
          />
        )}

        {notificationEmailError && (
          <AlertFloating color='error' title={notificationEmailError} />
        )}

        <ButtonList aria-label='Notification settings'>
          {/* Mention Notifications Toggle */}
          <ButtonList.Item
            trailing={
              <Toggle
                isSelected={formData.notify_on_mention}
                onChange={handleNotifyMentionToggle}
                isDisabled={
                  notifyMentionSaving ||
                  !!membershipLoadError ||
                  membershipId === null
                }
                aria-label='Toggle mention notifications'
              />
            }
          >
            <p className='text-sm font-medium text-fg-primary mb-0.5'>
              Mention notifications
            </p>
            <p className='text-sm text-fg-tertiary'>
              Notify me when I&apos;m mentioned in notes
            </p>
          </ButtonList.Item>

          {/* Owner Notifications Toggle */}
          <ButtonList.Item
            trailing={
              <Toggle
                isSelected={formData.notify_on_owned_objects}
                onChange={handleNotifyOwnedToggle}
                isDisabled={
                  notifyOwnedSaving ||
                  !!membershipLoadError ||
                  membershipId === null
                }
                aria-label='Toggle owner notifications'
              />
            }
          >
            <p className='text-sm font-medium text-fg-primary mb-0.5'>
              Owner notifications
            </p>
            <p className='text-sm text-fg-tertiary'>
              Notify me about records I own
            </p>
          </ButtonList.Item>

          {/* Digest Email Dropdown */}
          <ButtonList.Item
            trailing={
              <Select
                size='sm'
                className='w-48'
                aria-label='Digest email frequency'
                items={DIGEST_CADENCE_OPTIONS}
                selectedKey={formData.digest_cadence}
                onSelectionChange={handleDigestCadenceChange}
                isDisabled={
                  digestCadenceSaving ||
                  !!membershipLoadError ||
                  membershipId === null
                }
              >
                {item => <SelectItem id={item.id}>{item.label}</SelectItem>}
              </Select>
            }
          >
            <p className='text-sm font-medium text-fg-primary mb-0.5'>
              Digest email
            </p>
            <p className='text-sm text-fg-tertiary'>
              A summary of your top actions and meetings, delivered to your
              inbox.
            </p>
          </ButtonList.Item>

          {/* Notification Email Dropdown */}
          {distinctAccounts.length >= 1 && (
            <ButtonList.Item
              trailing={
                <Select
                  size='sm'
                  className='w-48'
                  aria-label='Notification email'
                  items={notificationEmailOptions}
                  selectedKey={notificationEmailSelectedKey}
                  onSelectionChange={handleNotificationEmailChange}
                  isDisabled={
                    notificationEmailSaving ||
                    !!membershipLoadError ||
                    membershipId === null
                  }
                >
                  {item => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              }
            >
              <p className='text-sm font-medium text-fg-primary mb-0.5'>
                Notification email
              </p>
              <p className='text-sm text-fg-tertiary'>
                Where to send account notifications.
              </p>
            </ButtonList.Item>
          )}

          {/* Push Notifications Toggle (renders null when unsupported) */}
          <NotificationSettings />
        </ButtonList>

        {/* Per-type notification channel routing */}
        {NOTIFICATION_TYPE_META.map(({ id: type, label, description }) => (
          <div key={type} role='group' aria-labelledby={`notif-type-${type}`}>
            <div className='mb-3'>
              <h3
                id={`notif-type-${type}`}
                className='text-sm font-medium text-fg-primary mb-0.5'
              >
                {label}
              </h3>
              <p className='text-sm text-fg-tertiary'>{description}</p>
            </div>
            <ButtonList aria-label={`${label} channels`}>
              {availableChannelsForType(type).map(channel => (
                <ButtonList.Item
                  key={channel}
                  trailing={
                    <Toggle
                      isSelected={isSelected(type, channel)}
                      onChange={channelHandlers[`${type}:${channel}`]}
                      isDisabled={
                        notificationChannelsSaving ||
                        !!membershipLoadError ||
                        membershipId === null
                      }
                      aria-label={`${label} – ${CHANNEL_LABELS[channel]} channel`}
                    />
                  }
                >
                  <p className='text-sm font-medium text-fg-primary'>
                    {CHANNEL_LABELS[channel]}
                  </p>
                </ButtonList.Item>
              ))}
            </ButtonList>
          </div>
        ))}
      </div>
    </div>
  );
}
